import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import jsQR from 'jsqr';
import { SwitchCamera } from 'lucide-react';

import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { dialogLgMaxWidthClassName } from '#/modules/commonComponents/dialog/dialogLayoutTokens';

import { parseEinvoiceQr } from '../helper/einvoiceQr';

/** 每隔多久解一次畫面（毫秒）；證明聯有左右兩個 QR，掃到右側（品項）就繼續掃 */
const SCAN_INTERVAL_MS = 200;

function getCameraErrorMessage(error) {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return '此網址無法使用相機：需要 https 或 localhost。';
  }
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
    return '相機權限被拒絕，請在瀏覽器網址列旁的權限設定允許相機後再試一次。';
  }
  if (error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError') {
    return '找不到可用的相機。';
  }
  if (error?.name === 'NotReadableError') {
    return '相機正被其他程式使用，請關閉後再試一次。';
  }
  return '無法開啟相機。';
}

/** 授權後才列得出完整鏡頭清單與名稱；未授權時 label 為空，以「鏡頭 N」代替 */
async function listVideoInputs() {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return [];
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === 'videoinput' && device.deviceId)
    .map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `鏡頭 ${index + 1}`,
    }));
}

/**
 * 開相機掃電子發票證明聯「左側 QR Code」。
 * 開啟時呼叫 getUserMedia（瀏覽器會詢問相機權限），預設優先後鏡頭；有兩顆以上鏡頭時右上角可切換。
 * 辨識成功回傳 { voucherNo, voucherDate, totalAmount } 並關閉。
 * 父層以 open 條件掛載，關閉即卸載並停止相機。
 */
const EinvoiceQrCameraDialog = ({ open, onOpenChange, onParsed }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const onParsedRef = useRef(onParsed);
  const [errorMessage, setErrorMessage] = useState('');
  const [isStarting, setIsStarting] = useState(true);
  const [cameras, setCameras] = useState([]);
  /** 使用者指定的鏡頭；null＝交給瀏覽器挑後鏡頭 */
  const [requestedDeviceId, setRequestedDeviceId] = useState(null);
  /** 實際開啟中的鏡頭（由 track settings 取得），用來決定「下一顆」 */
  const [activeDeviceId, setActiveDeviceId] = useState(null);

  useEffect(() => {
    onParsedRef.current = onParsed;
  }, [onParsed]);

  useEffect(() => {
    let stream = null;
    let attachedVideo = null;
    let timerId = null;
    let cancelled = false;

    function scanFrame() {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (cancelled || !video || !canvas) {
        return;
      }
      if (video.readyState >= video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(image.data, image.width, image.height, {
          inversionAttempts: 'dontInvert',
        });
        const parsed = code?.data ? parseEinvoiceQr(code.data) : null;
        if (parsed) {
          cancelled = true;
          onParsedRef.current(parsed);
          return;
        }
      }
      timerId = window.setTimeout(scanFrame, SCAN_INTERVAL_MS);
    }

    async function start() {
      try {
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error('insecure');
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: requestedDeviceId
            ? { deviceId: { exact: requestedDeviceId } }
            : { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const [track] = stream.getVideoTracks();
        setActiveDeviceId(track?.getSettings?.().deviceId ?? requestedDeviceId);
        listVideoInputs()
          .then((list) => {
            if (!cancelled) {
              setCameras(list);
            }
          })
          .catch(() => {});
        attachedVideo = videoRef.current;
        attachedVideo.srcObject = stream;
        await attachedVideo.play();
        setIsStarting(false);
        scanFrame();
      } catch (error) {
        if (!cancelled) {
          setIsStarting(false);
          setErrorMessage(getCameraErrorMessage(error));
        }
      }
    }

    start();

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
      stream?.getTracks().forEach((track) => track.stop());
      if (attachedVideo) {
        attachedVideo.srcObject = null;
      }
    };
  }, [requestedDeviceId]);

  const canSwitchCamera = cameras.length > 1;

  function switchToNextCamera() {
    const currentIndex = cameras.findIndex(
      (camera) => camera.deviceId === (activeDeviceId ?? requestedDeviceId),
    );
    const next = cameras[(currentIndex + 1) % cameras.length];
    setErrorMessage('');
    setIsStarting(true);
    setRequestedDeviceId(next.deviceId);
  }

  const activeCameraLabel = cameras.find((camera) => camera.deviceId === activeDeviceId)?.label;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="掃描發票 QR Code"
      description="請對準電子發票證明聯「左側」的 QR Code。"
      maxWidthClassName={dialogLgMaxWidthClassName}
      onCancel={() => onOpenChange(false)}
      cancelText="關閉"
    >
      {errorMessage ? (
        <div className="flex flex-col items-start gap-2">
          <p className="m-0 text-sm text-destructive">{errorMessage}</p>
          {canSwitchCamera ? (
            <Button type="button" variant="outline" size="sm" onClick={switchToNextCamera}>
              <SwitchCamera />
              改用其他鏡頭
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-md bg-black">
          <video
            ref={videoRef}
            className="block aspect-[4/3] w-full object-cover"
            muted
            playsInline
          />
          {isStarting ? (
            <p className="absolute inset-0 m-0 flex items-center justify-center text-sm text-white">
              正在開啟相機…
            </p>
          ) : null}
          {canSwitchCamera ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white"
              onClick={switchToNextCamera}
              disabled={isStarting}
              aria-label="切換鏡頭"
              title={activeCameraLabel ? `切換鏡頭（目前：${activeCameraLabel}）` : '切換鏡頭'}
            >
              <SwitchCamera />
            </Button>
          ) : null}
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" aria-hidden />
    </AppDialog>
  );
};

EinvoiceQrCameraDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onParsed: PropTypes.func.isRequired,
};

export default EinvoiceQrCameraDialog;
